import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const PostRevolucionIA = () => {
  const pageTitle =
    "“Revolución IA en SST”: El primer taller en Latinoamérica que transformará la seguridad laboral | Ernesto Mora";
  const canonical =
    "https://www.ernestomora.com/blog/post/revolucion-ia-sst";
  const metaDescription =
    "Descubre el primer Taller Intensivo de IA aplicada a la Seguridad y Salud en el Trabajo en Latinoamérica, liderado por Ernesto Mora. 100% práctico, con certificación ESGRYMA.";
  const keywords =
    "Revolución IA SST, taller IA SST, Ernesto Mora IA, inteligencia artificial seguridad laboral, IA en minería, IA en construcción, IA en manufactura, capacitación IA SST, certificación ESGRYMA, transformación digital SST, prevención con IA, automatización SST, deep learning seguridad, IA auditorías SST";

  const ogImage =
    "https://celebratufiesta.com.co/wp-content/uploads/2025/08/medellin.png";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "“Revolución IA en SST”: El primer taller en Latinoamérica que transformará la seguridad laboral",
    image: [ogImage],
    author: { "@type": "Person", name: "Ernesto Mora" },
    publisher: {
      "@type": "Organization",
      name: "Ernesto Mora",
      logo: {
        "@type": "ImageObject",
        url: "https://www.ernestomora.com/favicon-512.png",
      },
    },
    description: metaDescription,
    mainEntityOfPage: canonical,
  };

  return (
    <div className="min-h-screen bg-black text-white p-8 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] bg-repeat">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto">
        {/* Título */}
        <h1 className="text-4xl font-bold text-[#FFD100] mb-6">
          “Revolución IA en SST”: El primer taller en Latinoamérica que transformará la seguridad laboral, liderado por Ernesto Mora
        </h1>

        {/* Imagen portada */}
        <img
          src={ogImage}
          alt="Revolución IA en SST"
          className="w-2/3 mx-auto rounded-lg mb-6 shadow-lg"
          loading="lazy"
          decoding="async"
        />                   

        {/* Contenido original (sin cambios en texto) */}
        <section className="text-gray-300 leading-relaxed text-lg space-y-6">
          <p>
            La <strong className="text-[#FFD100]">Inteligencia Artificial (IA)</strong> está redefiniendo la manera en que trabajamos, prevenimos riesgos y protegemos vidas.
            En un mundo donde los accidentes laborales cuestan hasta el 5% del PIB de los países (OIT), y cada año se registran más de 2.3 millones de muertes laborales,
            la pregunta no es si necesitamos nuevas herramientas, sino: <em>¿cuán rápido podemos implementarlas?</em>
          </p>

          <p>
            Con esta visión, llega a Latinoamérica un evento pionero: el primer <strong className="text-[#FFD100]">Taller Intensivo de IA aplicada a Seguridad y Salud en el Trabajo (SST)</strong>, 
            diseñado e impartido por <strong>Ernesto Mora</strong>, uno de los mayores referentes en transformación digital y seguridad laboral en la región.
          </p>

          <h2 className="text-2xl font-bold text-[#FFD100] mt-8">
            ¿Por qué este taller es un hito en Latinoamérica?
          </h2>
          <p>
            Mientras el mundo empresarial avanza hacia la digitalización, la mayoría de los profesionales de SST aún gestionan riesgos con metodologías tradicionales.
            Sin embargo, la IA ya está demostrando resultados tangibles:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Reducción del 25% en incidentes laborales (McKinsey, 2025).</li>
            <li>Disminución del 40% en tiempos de auditoría mediante automatización de reportes.</li>
            <li>Precisión del 92-99% en diagnósticos médicos laborales con algoritmos de Deep Learning (NCBI, 2024).</li>
            <li>Ahorros económicos millonarios al transformar gastos reactivos en inversión preventiva.</li>
          </ul>

          <p>
            Este taller no será teoría académica, será acción inmediata. Desde el primer minuto, los participantes trabajarán con herramientas reales de IA diseñadas para 
            integrarse al marco regulatorio latinoamericano y aplicarse a sectores como <strong className="text-[#FFD100]">minería, construcción, energía y manufactura</strong>.
          </p>

          <h2 className="text-2xl font-bold text-[#FFD100] mt-8">
            Ernesto Mora: liderazgo e innovación al servicio de la seguridad laboral
          </h2>
          <p>
            El taller estará dirigido por <strong>Ernesto Mora Soto</strong>, CEO de ESGRYMA Consulting Group, MBA, speaker internacional y referente en seguridad laboral en LATAM.
            Con más de 20 años de experiencia, Ernesto ha liderado transformaciones en más de 1,000 empresas, logrando reducciones sostenibles de accidentes y fortaleciendo culturas de seguridad corporativa.
          </p>
          <p>
            Su trayectoria lo convierte en un pionero al unir inteligencia artificial, gestión SST y liderazgo humano. Conoce más sobre su trabajo en{" "}
            <a
              href="https://ernestomora.com"
              target="_blank"
              rel="noreferrer"
              className="text-[#FFD100] hover:underline font-semibold"
            >
              ernestomora.com
            </a>.
          </p>

          <h2 className="text-2xl font-bold text-[#FFD100] mt-8">
            Lo que aprenderás en el Taller “Revolución IA en SST”
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Configurar herramientas clave de IA (ChatGPT, Claude, Gemini).</li>
            <li>Automatizar matrices de riesgo en minutos.</li>
            <li>Realizar investigaciones de accidentes completas en menos de 15 minutos.</li>
            <li>Crear informes inteligentes de inspección y auditoría.</li>
            <li>Diseñar campañas de seguridad persuasivas con IA.</li>
            <li>Elaborar un plan de implementación de 30 días para llevar la IA a sus organizaciones.</li>
          </ul>

          <p>
            Todo acompañado de casos prácticos de empresas latinoamericanas que ya están aplicando IA en minería, construcción y manufactura.
          </p>

          <h2 className="text-2xl font-bold text-[#FFD100] mt-8">Características clave</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>100% práctico – cero teoría aburrida.</li>
            <li>Exclusivo para profesionales SST – contenido adaptado al marco HSEQ regional.</li>
            <li>Acceso limitado – máximo 50 personas presenciales y 100 virtuales.</li>
            <li>Certificación ESGRYMA – válida para recertificaciones y acceso a comunidad exclusiva IA-SST.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#FFD100] mt-8">Una oportunidad que no se repetirá</h2>
          <p>
            La ventana de oportunidad es clara: en los próximos 12 a 18 meses, las organizaciones que integren IA en SST capturarán una ventaja competitiva decisiva
            en seguridad, productividad y reputación corporativa.
          </p>
          <p>
            Este taller es más que una capacitación: es el inicio de una nueva era para los profesionales de SST en Latinoamérica.
          </p>

          <p><strong>Fecha y horario:</strong> 8:00 AM - 5:00 PM (Hora Colombia)</p>
          <p><strong>Modalidad:</strong> Presencial (con cupos virtuales)</p>
          <p><strong>Certificación:</strong> ESGRYMA Consulting Group</p>
          <p>
            Más información en{" "}
            <a
              href="https://ernestomora.com"
              target="_blank"
              rel="noreferrer"
              className="text-[#FFD100] hover:underline font-semibold"
            >
              ernestomora.com
            </a>
          </p>

          <h2 className="text-2xl font-bold text-[#FFD100] mt-8">Reflexión final</h2>
          <p>
            La IA no viene a reemplazar a los profesionales de SST, viene a potenciarlos. 
            El verdadero reto no es si adoptarla, sino qué tan rápido estarás preparado para liderar con ella.
          </p>
        </section>

        {/* Botón regreso */}
        <div className="text-center mt-10">
          <Link
            to="/blogernesto"
            className="inline-block bg-[#FFD100] text-black px-6 py-3 rounded-lg hover:bg-yellow-400 font-bold text-lg transition-transform transform hover:scale-105"
          >
            ← Volver al Blog
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PostRevolucionIA;
